import React from 'react';
import { Modal, Button, Select, Checkbox, Tooltip, Tag, Alert } from 'antd';
import {
  DownOutlined,
  QuestionCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
  isLanguageBeta,
  BetaTag,
} from '../LanguageConfig';

type Tier = 'Good' | 'Better' | 'Best';

type DemoModel = {
  id: string;
  label: string;
  tags?: string[];
  defaultTier?: Tier;
  languageTierOverrides?: Record<string, Tier>;
};

const TIER_TAG_STYLE: Record<Tier, React.CSSProperties> = {
  Best: { backgroundColor: '#FFE7BA', color: '#AD6800', borderColor: '#AD6800' },
  Better: { backgroundColor: '#BAE7FF', color: '#0958D9', borderColor: '#0958D9' },
  Good: { backgroundColor: '#D9F7BE', color: '#237804', borderColor: '#237804' },
};

const HARD_MODELS: DemoModel[] = [
  { id: 'Chat-GPT', label: 'Chat-GPT', tags: ['Legacy'] },
  {
    id: 'ChatGPT4.1',
    label: 'ChatGPT 4.1',
    defaultTier: 'Better',
    languageTierOverrides: {
      Ukrainian: 'Good',
      Polish: 'Good',
      'Brazilian Portuguese': 'Best',
    },
  },
  {
    id: 'ChatGPT5',
    label: 'ChatGPT 5',
    defaultTier: 'Best',
  },
  { id: 'Claude', label: 'Claude 3.5', tags: ['Legacy'] },
  { id: 'Claude3', label: 'Claude 3', tags: ['Legacy'] },
  {
    id: 'Claude3.7',
    label: 'Claude 3.7',
    defaultTier: 'Better',
    languageTierOverrides: {
      German: 'Best',
      'Brazilian Portuguese': 'Best',
    },
  },
  { id: 'DeepSeek', label: 'DeepSeek', tags: ['Legacy', '50% OFF'] },
  {
    id: 'DeepSeekR10528',
    label: 'DeepSeek R1',
    tags: ['50% OFF'],
    defaultTier: 'Good',
  },
  {
    id: 'Gemini2.5Pro',
    label: 'Gemini 2.5 Pro',
    defaultTier: 'Best',
    languageTierOverrides: {
      German: 'Best',
      'Brazilian Portuguese': 'Good',
    },
  },
];

const ALLOWED_MODEL_IDS = new Set(['ChatGPT5', 'Gemini2.5Pro', 'Claude3.7']);
const ALLOWED_LANGUAGES = new Set(['French', 'German']);

interface DemoKeywordSuggestionModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuggest: (targetLanguage: string, selectedOptions: { genre: boolean; tropes: boolean; keywords: boolean; blurb: boolean }) => void;
  bookLanguage: string;
  initialTargetLanguage?: string;
  genres: string[];
  tropes: string[];
  keywords: string[];
  blurb: string;
  languages: string[];
  resetCounter: number;
}

const DemoKeywordSuggestionModal: React.FC<DemoKeywordSuggestionModalProps> = ({
  visible,
  onCancel,
  onSuggest,
  bookLanguage,
  initialTargetLanguage,
  genres,
  tropes,
  keywords,
  blurb,
  languages,
  resetCounter,
}) => {
  const [targetLang, setTargetLang] = React.useState<string>(initialTargetLanguage || '');
  const [selectedAIModel, setSelectedAIModel] = React.useState<string>('');
  const [selectedOptions, setSelectedOptions] = React.useState({
    genre: false,
    tropes: false,
    keywords: false,
    blurb: false,
  });

  // Credits (demo) - load from /demo/credits.json and reset on Demo reset
  const [creditBalance, setCreditBalance] = React.useState<number | null>(null);
  const [initialCreditBalance, setInitialCreditBalance] = React.useState<number>(150000);

  React.useEffect(() => {
    let isMounted = true;
    const loadCredits = async () => {
      try {
        const res = await fetch('/demo/credits.json', { cache: 'no-store' });
        if (!res.ok) throw new Error(`Failed to load credits.json: ${res.status}`);
        const data = await res.json();
        if (isMounted && typeof data?.credit_balance === 'number') {
          setCreditBalance(data.credit_balance);
          setInitialCreditBalance(data.credit_balance);
        }
      } catch {
        if (isMounted) {
          setCreditBalance(150000);
          setInitialCreditBalance(150000);
        }
      }
    };
    loadCredits();
    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    // Reset demo credits to 150,000 on page reset
    setInitialCreditBalance(150000);
    setCreditBalance(150000);
    // Also reset modal selections upon reset
    setTargetLang(initialTargetLanguage || '');
    setSelectedOptions({ genre: false, tropes: false, keywords: false, blurb: false });
  }, [resetCounter, initialTargetLanguage]);

  // Sort and tag models like DemoMetadataTranslationModal
  const getEffectiveTier = (model: DemoModel, lang?: string): Tier | undefined => {
    if (lang && model.languageTierOverrides && model.languageTierOverrides[lang]) {
      return model.languageTierOverrides[lang];
    }
    return model.defaultTier;
  };

  const sortedModels = React.useMemo(() => {
    const tierOrder: Record<string, number> = { Best: 0, Better: 1, Good: 2, Legacy: 3 };
    const getCategory = (m: DemoModel): 'Best' | 'Better' | 'Good' | 'Legacy' => {
      const t = getEffectiveTier(m, targetLang || undefined);
      return (t as 'Best' | 'Better' | 'Good') || 'Legacy';
    };
    const arr = [...HARD_MODELS];
    arr.sort((a, b) => {
      const ca = getCategory(a);
      const cb = getCategory(b);
      if (tierOrder[ca] !== tierOrder[cb]) return tierOrder[ca] - tierOrder[cb];
      return a.label.localeCompare(b.label);
    });
    return arr;
  }, [targetLang]);

  const renderTierTag = (tier: Tier) => (
    <Tag style={{ ...TIER_TAG_STYLE[tier], marginLeft: 8 }}>
      {tier}
    </Tag>
  );

  const renderModelLabel = (m: DemoModel) => {
    const tier = getEffectiveTier(m, targetLang || undefined);
    return (
      <span>
        {m.label}
        {tier ? renderTierTag(tier) : null}
        {(m.tags || []).map((t) => {
          const isDiscount = t.toLowerCase().includes('off');
          const isLegacy = t.toLowerCase().includes('legacy');
          if (isLegacy) {
            return (
              <Tag
                key={t}
                style={{ backgroundColor: '#ffd6e7', color: '#9e1068', borderColor: '#9e1068', marginLeft: 8 }}
              >
                {t}
              </Tag>
            );
          }
          return (
            <Tag key={t} color={isDiscount ? 'green' : 'blue'} style={{ marginLeft: 8 }}>
              {t}
            </Tag>
          );
        })}
      </span>
    );
  };

  // Auto-select a default allowed model based on tier for chosen language
  React.useEffect(() => {
    if (!targetLang) return;
    const current = HARD_MODELS.find((m) => m.id === selectedAIModel);
    const currentAllowed = !!current && ALLOWED_MODEL_IDS.has(current.id);
    if (currentAllowed) return;

    const findByTier = (tier: Tier) =>
      HARD_MODELS.find(
        (m) => ALLOWED_MODEL_IDS.has(m.id) && getEffectiveTier(m, targetLang) === tier
      );
    const desired =
      findByTier('Best') ||
      findByTier('Better') ||
      findByTier('Good') ||
      HARD_MODELS.find((m) => ALLOWED_MODEL_IDS.has(m.id));

    if (desired) {
      setSelectedAIModel(desired.id);
    }
  }, [targetLang, selectedAIModel]);

  // Helpers
  const isAnySelected = selectedOptions.genre || selectedOptions.tropes || selectedOptions.keywords || selectedOptions.blurb;
  const calculateDemoCost = React.useCallback(() => (isAnySelected ? 500 : 0), [isAnySelected]);

  const hasEnoughCredits =
    creditBalance !== null && creditBalance >= calculateDemoCost();

  const disabledGenre = (genres || []).length === 0;
  const disabledTropes = (tropes || []).length === 0;
  const disabledKeywords = (keywords || []).length === 0;
  const disabledBlurb = !blurb || blurb.length === 0;

  const getStatusMessages = () => {
    const messages: {
      type: 'error' | 'warning';
      message: string;
      icon: React.ReactNode;
    }[] = [];

    if (!targetLang) {
      messages.push({
        type: 'error',
        message: 'Please select a target language to proceed.',
        icon: <ExclamationCircleOutlined />,
      });
    }
    if (!isAnySelected) {
      messages.push({
        type: 'error',
        message: 'Please select at least one data source for keyword generation.',
        icon: <ExclamationCircleOutlined />,
      });
    }
    if (!hasEnoughCredits) {
      messages.push({
        type: 'error',
        message: 'You do not have enough credits for this operation.',
        icon: <ExclamationCircleOutlined />,
      });
    }
    if (
      (genres || []).length === 0 &&
      (tropes || []).length === 0 &&
      (keywords || []).length === 0 &&
      (!blurb || blurb.length === 0)
    ) {
      messages.push({
        type: 'warning',
        message:
          'No metadata is available. Add genre, tropes, keywords, or blurb to your book for better results.',
        icon: <ExclamationCircleOutlined />,
      });
    }

    return messages;
  };

  const handleGenerate = () => {
    const cost = calculateDemoCost();
    if (!targetLang || !isAnySelected || creditBalance === null || creditBalance < cost) return;
    // Deduct credits locally
    setCreditBalance((prev) => (typeof prev === 'number' ? prev - cost : prev));
    // Delegate to parent to simulate generation
    onSuggest(targetLang, selectedOptions);
  };

  return (
    <Modal
      title="Keyword Suggestions"
      visible={visible}
      onCancel={onCancel}
      width={760}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button
          key="generate"
          type="primary"
          onClick={handleGenerate}
          disabled={!targetLang || !isAnySelected || !hasEnoughCredits}
          data-testid="tutorial:keyword-suggestions:generate"
        >
          Generate Keywords
        </Button>,
      ]}
    >
      <div data-testid="tutorial:keyword-suggestions:modal">
      {/* Origin and Target language */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15, marginBottom: 15 }}>
        <div data-testid="tutorial:keyword-suggestions:target-language">
          <label style={{ display: 'block', marginBottom: 5 }}>
            <strong>Origin Language:</strong>
          </label>
          <div>{bookLanguage}</div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 5 }}>
            <strong>Target Language:</strong>
          </label>
          <Select
            value={targetLang}
            onChange={setTargetLang}
            style={{ width: '100%' }}
            placeholder="Select a target language"
            suffixIcon={
              <span className="ant-select-suffix">
                <DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }} />
              </span>
            }
          >
            {languages
              .filter((lang) => lang !== bookLanguage)
              .map((lang) => (
                <Select.Option key={lang} value={lang} disabled={!ALLOWED_LANGUAGES.has(lang)}>
                  <span>
                    {lang}
                    {isLanguageBeta(lang) && <BetaTag />}
                  </span>
                </Select.Option>
              ))}
          </Select>
        </div>
      </div>

      {/* AI Model */}
      <div data-testid="tutorial:keyword-suggestions:model" style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', marginBottom: 5 }}>
          <strong>AI Model:</strong>
        </label>
        <Select
          value={targetLang ? (selectedAIModel || undefined) : undefined}
          onChange={setSelectedAIModel}
          style={{ width: '100%' }}
          placeholder={targetLang ? 'Select an AI Model' : 'Choose a Language to select an AI Model'}
          disabled={!targetLang}
          suffixIcon={
            <span className="ant-select-suffix">
              <DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }} />
            </span>
          }
        >
          {sortedModels.map((m) => (
            <Select.Option key={m.id} value={m.id} disabled={!ALLOWED_MODEL_IDS.has(m.id)}>
              {renderModelLabel(m)}
            </Select.Option>
          ))}
        </Select>
      </div>

      {/* Data Sources */}
      <div
        data-testid="tutorial:keyword-suggestions:data-sources"
        style={{
          background: '#f7f7f7',
          padding: 12,
          borderRadius: 6,
          marginBottom: 12,
        }}
      >
        <div style={{ marginBottom: 6 }}>
          <strong>Data Sources for Keyword Generation</strong>
        </div>
        <p>
          Check what data you want ScribeShadow to use when suggesting keywords. For best results,
          populate Keywords, Genre, and Tropes in your metadata.
        </p>
        <Tooltip title={disabledGenre ? 'Genre has not been provided.' : ''}>
          <Checkbox
            checked={selectedOptions.genre}
            onChange={(e) => setSelectedOptions({ ...selectedOptions, genre: e.target.checked })}
            disabled={disabledGenre}
          >
            Genre
          </Checkbox>
        </Tooltip>
        <br />
        <Tooltip title={disabledTropes ? 'Tropes have not been provided.' : ''}>
          <Checkbox
            checked={selectedOptions.tropes}
            onChange={(e) => setSelectedOptions({ ...selectedOptions, tropes: e.target.checked })}
            disabled={disabledTropes}
          >
            Tropes
          </Checkbox>
        </Tooltip>
        <br />
        <Tooltip title={disabledKeywords ? 'Keywords have not been provided.' : ''}>
          <Checkbox
            checked={selectedOptions.keywords}
            onChange={(e) => setSelectedOptions({ ...selectedOptions, keywords: e.target.checked })}
            disabled={disabledKeywords}
          >
            {bookLanguage} Keywords
          </Checkbox>
        </Tooltip>
        <br />
        <Tooltip title={disabledBlurb ? 'Blurb has not been provided.' : ''}>
          <Checkbox
            checked={selectedOptions.blurb}
            onChange={(e) => setSelectedOptions({ ...selectedOptions, blurb: e.target.checked })}
            disabled={disabledBlurb}
          >
            Blurb
          </Checkbox>
        </Tooltip>
      </div>

      {/* Credit info */}
      <div
        data-testid="tutorial:keyword-suggestions:cost-readiness"
        style={{
          background: '#f0f7ff',
          padding: 12,
          borderRadius: 6,
          marginBottom: 12,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong>Credit Balance:</strong>
          <span>{creditBalance !== null ? creditBalance : 'Loading...'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
          <strong>Credit Cost:</strong>
          <span style={{ marginLeft: 5, marginRight: 5 }}>{calculateDemoCost()}</span>
          <Tooltip title="The cost for generating keyword suggestions">
            <QuestionCircleOutlined style={{ color: '#1677ff' }} />
          </Tooltip>
        </div>
      </div>

      {/* Alerts */}
      <div data-testid="tutorial:keyword-suggestions:status-messages" style={{ marginTop: 8 }}>
        {getStatusMessages().map((m, idx) => (
          <Alert
            key={idx}
            message={m.message}
            type={m.type}
            icon={<ExclamationCircleOutlined />}
            showIcon
            style={{ marginBottom: 8 }}
          />
        ))}
      </div>
      </div>
    </Modal>
  );
};

export default DemoKeywordSuggestionModal;
